import React, {useEffect, useState} from "react";
import {Box, Button, CircularProgress, Typography, useMediaQuery} from "@mui/material";
import {useMe} from "../common/useMe";
import AssistantFab from "../assistant/AssistantFab";
import AssistantPanel from "../assistant/shell/AssistantPanel";
import ScreenshotCaptureButton from "../assistant/screenshot/ScreenshotCaptureButton";
import {
    ASSISTANT_FAB_MARGIN,
    ASSISTANT_FAB_MOBILE_MARGIN,
    ASSISTANT_FAB_MOBILE_SIZE,
    ASSISTANT_FAB_SIZE,
    ASSISTANT_PANEL_GAP,
} from "../assistant/constants";

const REVEAL_DURATION_MS = 500;
const showAnnotationTool = process.env.REACT_APP_SHOW_ANNOTATION_TOOL === "true";

type SplashPhase = "loading" | "revealing" | "ready";

interface Props {
    children: React.ReactNode;
}

export function DashboardShell({children}: Props) {
    const {isError, isReady} = useMe();
    const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
    const [phase, setPhase] = useState<SplashPhase>(isReady ? "ready" : "loading");

    useEffect(() => {
        if (!isReady) {
            setPhase("loading");
            return;
        }

        // Reduced motion skips the reveal phase entirely: no timer, no fade.
        if (prefersReducedMotion) {
            setPhase("ready");
            return;
        }

        setPhase(current => current === "ready" ? "ready" : "revealing");
    }, [isReady, prefersReducedMotion]);

    useEffect(() => {
        if (phase !== "revealing") {
            return undefined;
        }

        const revealTimer = window.setTimeout(() => setPhase("ready"), REVEAL_DURATION_MS);

        return () => window.clearTimeout(revealTimer);
    }, [phase]);

    const dashboardVisible = phase !== "loading";
    const splashFadingOut = phase === "revealing";

    return (
        <Box
            sx={{
                position: "relative",
                minHeight: "100dvh",
                bgcolor: "background.default",
            }}
        >
            <Box
                sx={{
                    minHeight: "100dvh",
                    opacity: dashboardVisible ? 1 : 0,
                    transform: dashboardVisible || prefersReducedMotion ? "none" : "scale(0.997)",
                    transition: prefersReducedMotion
                        ? "none"
                        : `opacity ${REVEAL_DURATION_MS}ms ease, transform ${REVEAL_DURATION_MS}ms ease`,
                }}
            >
                {children}
            </Box>

            {showAnnotationTool && (
                <Box
                    className="hide-on-print"
                    sx={{
                        position: "fixed",
                        bottom: {xs: ASSISTANT_FAB_MOBILE_MARGIN, sm: ASSISTANT_FAB_MARGIN},
                        right: {
                            xs: ASSISTANT_FAB_MOBILE_MARGIN + ASSISTANT_FAB_MOBILE_SIZE + ASSISTANT_PANEL_GAP,
                            sm: ASSISTANT_FAB_MARGIN + ASSISTANT_FAB_SIZE + ASSISTANT_PANEL_GAP,
                        },
                        zIndex: 1249,
                    }}
                >
                    <ScreenshotCaptureButton/>
                </Box>
            )}
            <AssistantPanel/>
            <AssistantFab/>

            {phase !== "ready" && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        bgcolor: "background.default",
                        zIndex: theme => theme.zIndex.modal + 1,
                        opacity: splashFadingOut ? 0 : 1,
                        pointerEvents: splashFadingOut ? "none" : "auto",
                        transition: `opacity ${REVEAL_DURATION_MS}ms ease`,
                    }}
                >
                    {isError ? (
                        <>
                            <Typography color="text.secondary">
                                Something went wrong while loading your dashboard.
                            </Typography>
                            <Button variant="outlined" onClick={() => window.location.reload()}>
                                Reload
                            </Button>
                        </>
                    ) : (
                        <CircularProgress size={32}/>
                    )}
                </Box>
            )}
        </Box>
    );
}
